import React, { useState, useEffect, useCallback } from 'react';

const LeaveApply = ({ onNavigate, userInfo }) => {
  const [formData, setFormData] = useState({
    leaveType: '',
    fromDate: '',
    toDate: '',
    reason: '',
    contactNumber: '',
    addressDuringLeave: '',
    emergencyContact: '',
    attachment: null
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showPreviousApplications, setShowPreviousApplications] = useState(false);
  const [previousApplications, setPreviousApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // API Base URL - adjust this based on your backend configuration
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

  const leaveTypes = [
    { value: 'casual', label: 'Casual Leave', icon: '🏖️' },
    { value: 'medical', label: 'Medical Leave', icon: '🏥' },
    { value: 'emergency', label: 'Emergency Leave', icon: '🚨' },
    { value: 'academic', label: 'Academic Leave', icon: '📚' }
  ];

  // Fetch previous applications
  const fetchPreviousApplications = useCallback(async () => {
    setLoadingApplications(true);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const userId = userInfo?.userId || userInfo?.id;

      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await fetch(`${API_BASE_URL}/student/leave/applications/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }

      const result = await response.json();

      if (result.success) {
        setPreviousApplications(result.applications || []);
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
      setError('Failed to load previous applications');
    } finally {
      setLoadingApplications(false);
    }
  }, [userInfo, API_BASE_URL]);

  // Load previous applications when view is toggled
  useEffect(() => {
    if (showPreviousApplications && previousApplications.length === 0) {
      fetchPreviousApplications();
    }
  }, [showPreviousApplications, previousApplications.length, fetchPreviousApplications]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size should not exceed 5MB');
        return;
      }
      setFormData(prev => ({
        ...prev,
        attachment: file
      }));
      setError(null);
    }
  };
  const getRejectionInfo = (application) => {
    // Check which level rejected
    if (application.parentApprovalStatus === 'rejected') {
      return {
        level: 'Parent',
        rejectedBy: 'Parent',
        remarks: application.parentRemarks || application.parent_rejection_reason || 'No reason provided',
        rejectedAt: application.parentApprovedAt
      };
    }

    if (application.level1ApprovalStatus === 'rejected') {
      return {
        level: 'Level 1 (Warden)',
        rejectedBy: application.level1ApprovedByName || 'Level 1 Admin',
        remarks: application.level1Remarks || 'No reason provided',
        rejectedAt: application.level1ApprovedAt
      };
    }

    if (application.level2ApprovalStatus === 'rejected') {
      return {
        level: 'Level 2 (Final Admin)',
        rejectedBy: application.level2ApprovedByName || 'Level 2 Admin',
        remarks: application.level2Remarks || 'No reason provided',
        rejectedAt: application.level2ApprovedAt
      };
    }

    // Legacy admin rejection
    if (application.adminApprovalStatus === 'rejected') {
      return {
        level: 'Admin',
        rejectedBy: application.adminApprovedByName || 'Administrator',
        remarks: application.adminRemarks || 'No reason provided',
        rejectedAt: application.adminApprovedAt
      };
    }

    return null;
  };

  const getApprovalTimeline = (application) => {
    const timeline = [];

    // Parent approval
    if (application.parentApprovalStatus === 'approved') {
      timeline.push({
        level: 'Parent',
        status: 'approved',
        icon: '✓',
        label: 'Parent approved',
        date: application.parentApprovedAt
      });
    }

    // Level 1 approval
    if (application.level1ApprovalStatus === 'approved') {
      timeline.push({
        level: 'Level 1',
        status: 'approved',
        icon: '✓',
        label: 'Level 1 approved',
        date: application.level1ApprovedAt
      });
    }

    // Level 2 approval
    if (application.level2ApprovalStatus === 'approved') {
      timeline.push({
        level: 'Level 2',
        status: 'approved',
        icon: '✓',
        label: 'Level 2 approved',
        date: application.level2ApprovedAt
      });
    }

    return timeline;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.leaveType) {
      setError('Please select a leave type');
      return;
    }
    // Validation
    if (!formData.leaveType || !formData.fromDate || !formData.toDate || !formData.reason) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(formData.fromDate) > new Date(formData.toDate)) {
      setError('To date cannot be before from date');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const userId = userInfo?.userId || userInfo?.id;

      if (!userId) {
        throw new Error('User ID not found');
      }

      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });
      submitData.append('studentId', userId);

      const response = await fetch(`${API_BASE_URL}/student/leave/apply`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : undefined
        },
        body: submitData
      });

      if (!response.ok) {
        throw new Error(`Failed to submit leave application: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
        setFormData({
          leaveType: '',
          fromDate: '',
          toDate: '',
          reason: '',
          contactNumber: '',
          addressDuringLeave: '',
          emergencyContact: '',
          attachment: null
        });
        // Reset file input
        const fileInput = document.getElementById('attachment');
        if (fileInput) fileInput.value = '';

        // Refresh previous applications list
        if (showPreviousApplications) {
          fetchPreviousApplications();
        }
      } else {
        throw new Error(result.message || 'Failed to submit leave application');
      }
    } catch (err) {
      console.error('Error submitting leave application:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return '#10b981'; // Green
      case 'rejected':
      case 'cancelled':
        return '#ef4444'; // Red
      case 'pending_parent':
        return '#f59e0b'; // Orange
      case 'pending_level1':
        return '#f59e0b'; // Orange
      case 'pending_level2':
        return '#3b82f6'; // Blue
      case 'pending_admin':
        return '#f59e0b'; // Orange (legacy support)
      default:
        return '#6b7280'; // Gray
    }
  };
  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending_parent':
        return 'Pending Parent Approval';
      case 'pending_level1':
        return 'Pending_level1'; // Warden/Level 1 approval
      case 'pending_level2':
        return 'Pending Level 2'; // Final admin approval
      case 'pending_admin':
        return 'Pending Admin Approval'; // Legacy support
      case 'approved':
        return 'Approved';
      case 'rejected':
        return 'Rejected';
      case 'cancelled':
        return 'Cancelled';
      default:
        return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';

    try {
      // Handle both date strings and date objects
      const date = new Date(dateString);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      // Format as DD/MM/YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'N/A';
    }
  };
  const calculateDuration = (fromDate, toDate) => {
    if (!fromDate || !toDate) return 'N/A';

    try {
      const from = new Date(fromDate);
      const to = new Date(toDate);

      if (isNaN(from.getTime()) || isNaN(to.getTime())) {
        return 'N/A';
      }

      const diffTime = Math.abs(to - from);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both days

      return diffDays === 1 ? '1 day' : `${diffDays} days`;
    } catch (error) {
      console.error('Error calculating duration:', error);
      return 'N/A';
    }
  };


  const MenuItem = ({ icon, label, active = false, route }) => (
    <div
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setSidebarOpen(false); // Close mobile menu on navigation
      }}
    >
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  return (
    <>
      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: linear-gradient(135deg, #f2f8f7 0%, #c7e5e1 100%);
          overflow-x: hidden;
        }

        .dashboard-container {
          min-height: 100vh;
          display: flex;
          position: relative;
        }

        /* Sidebar */
        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 300px;
          background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
          backdrop-filter: blur(30px);
          border-right: 2px solid rgba(82,171,152,0.2);
          transform: translateX(0);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 100;
          display: flex;
          flex-direction: column;
          box-shadow: 8px 0 40px rgba(43,103,119,0.15);
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 36px 28px;
          background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
          border-bottom: 2px solid rgba(82,171,152,0.15);
          position: relative;
          overflow: hidden;
        }

        .sidebar-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
          animation: shimmer 4s infinite;
        }

        .sidebar-top {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          margin-bottom: 24px;
          position: relative;
          z-index: 1;
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 14px;
          position: relative;
          z-index: 1;
        }

        .user-avatar-sidebar {
          width: 56px;
          height: 56px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 22px;
          color: white;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
          border: 3px solid rgba(255, 255, 255, 0.5);
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          padding: 28px 20px;
          overflow-y: auto;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          color: #2b6777;
          font-size: 15px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s;
          overflow: hidden;
        }

        .menu-glow {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          opacity: 0;
          transition: opacity 0.3s;
        }

        .menu-item:hover {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .menu-item:hover .menu-glow {
          opacity: 1;
        }

        .menu-item-active {
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
          color: #2b6777;
          box-shadow: 0 6px 20px rgba(82,171,152,0.2);
          font-weight: 700;
        }

        .menu-item-active .menu-glow {
          opacity: 1;
          animation: glow 2s ease-in-out infinite;
        }

        .menu-icon {
          font-size: 22px;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          position: relative;
          z-index: 1;
        }

        .logout-section {
          padding: 20px;
          border-top: 2px solid rgba(82,171,152,0.15);
        }

        .logout-btn {
          width: 100%;
          padding: 16px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .logout-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 35px rgba(43,103,119,0.4);
        }

        /* Overlay */
        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.4);
          backdrop-filter: blur(6px);
          z-index: 90;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }

        .overlay.show {
          opacity: 1;
          visibility: visible;
        }

        .overlay.notifications-overlay {
          z-index: 999;
        }

        /* Main Content */
        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          width: 100%;
          position: relative;
          z-index: 1;
          margin-left: 300px;
        }

        .top-header {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          padding: 18px 24px;
          border-radius: 18px;
          box-shadow: 0 6px 24px rgba(43,103,119,0.08);
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
          border: 2px solid rgba(82,171,152,0.12);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 18px;
        }

        .mobile-menu-toggle {
          display: none;
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px solid rgba(82,171,152,0.2);
          cursor: pointer;
          font-size: 22px;
          color: #2b6777;
          width: 44px;
          height: 44px;
          border-radius: 12px;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .mobile-menu-toggle:hover {
          background: linear-gradient(135deg, rgba(43,103,119,0.2) 0%, rgba(82,171,152,0.2) 100%);
          border-color: #52ab98;
          transform: scale(1.05);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-greeting {
          font-size: 13px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .header-name {
          font-size: 20px;
          font-weight: 800;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .user-avatar-header {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 16px;
          color: white;
          box-shadow: 0 6px 20px rgba(43,103,119,0.25);
          border: 3px solid rgba(255, 255, 255, 0.8);
        }

        .content-wrapper {
          padding: 4px;
        }

        .hero-section {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 24px;
          padding: 40px 36px;
          margin-bottom: 28px;
          box-shadow: 0 12px 48px rgba(43,103,119,0.3);
          position: relative;
          overflow: hidden;
        }

        .hero-section::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          filter: blur(60px);
        }

        .hero-content {
          position: relative;
          z-index: 1;
        }

        .hero-title {
          font-size: 28px;
          font-weight: 900;
          color: white;
          margin-bottom: 10px;
          letter-spacing: -0.5px;
        }

        .hero-subtitle {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.9);
          font-weight: 500;
        }

        /* View Toggle Button */
        .view-toggle-btn {
          margin-bottom: 24px;
          padding: 14px 24px;
          background: white;
          border: 2px solid rgba(43,103,119,0.2);
          border-radius: 12px;
          color: #2b6777;
          font-weight: 600;
          font-size: 15px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .view-toggle-btn:hover {
          background: rgba(43,103,119,0.05);
          border-color: rgba(43,103,119,0.3);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(43,103,119,0.15);
        }

        /* Success/Error Messages */
        .success-message, .error-message {
          padding: 16px 20px;
          border-radius: 12px;
          margin-bottom: 24px;
          display: flex;
          align-items: center;
          gap: 12px;
          animation: slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .success-message {
          background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
          border: 1px solid #6ee7b7;
        }

        .error-message {
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          border: 1px solid #fca5a5;
        }

        .success-icon, .error-icon {
          font-size: 24px;
        }

        .success-text, .error-text {
          flex: 1;
          font-size: 14px;
          font-weight: 500;
        }

        .success-text {
          color: #047857;
        }

        .error-text {
          color: #dc2626;
        }

        /* Form Section */
        .form-section {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
          margin-bottom: 28px;
        }

        .form-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 32px;
          padding-bottom: 24px;
          border-bottom: 2px solid rgba(43,103,119,0.1);
        }

        .form-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          background: linear-gradient(135deg, #c2edce 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
        }

        .form-title {
          font-size: 24px;
          font-weight: 700;
          color: #2b6777;
        }

        /* Leave Type Selection */
        .leave-type-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }

        .leave-type-option {
          padding: 20px;
          border: 2px solid rgba(43,103,119,0.15);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: white;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .leave-type-option:hover {
          border-color: rgba(43,103,119,0.3);
          transform: translateY(-4px);
          box-shadow: 0 8px 20px rgba(43,103,119,0.1);
        }

        .leave-type-option.selected {
          border-color: #52ab98;
          background: linear-gradient(135deg, rgba(194,237,206,0.2) 0%, rgba(82,171,152,0.1) 100%);
          box-shadow: 0 8px 20px rgba(43,103,119,0.15);
        }

        .leave-type-icon {
          font-size: 32px;
          margin-bottom: 8px;
        }

        .leave-type-label {
          font-size: 16px;
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .leave-type-desc {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
          line-height: 1.4;
        }

        /* Form Fields */
        .form-grid {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .form-row {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
        }

        .form-label.required::after {
          content: ' *';
          color: #ef4444;
        }

        .form-input, .form-textarea {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid rgba(43,103,119,0.15);
          border-radius: 10px;
          font-size: 14px;
          color: #2b6777;
          transition: all 0.3s;
          font-family: inherit;
          background: white;
        }

        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: #52ab98;
          box-shadow: 0 0 0 3px rgba(82,171,152,0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        /* File Input */
        .file-input-wrapper {
          position: relative;
        }

        .file-input {
          display: none;
        }

        .file-input-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 32px;
          border: 2px dashed rgba(43,103,119,0.3);
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s;
          background: rgba(43,103,119,0.02);
          gap: 8px;
        }

        .file-input-label:hover {
          border-color: #52ab98;
          background: rgba(82,171,152,0.05);
        }

        .file-input-label i {
          font-size: 32px;
        }

        .file-input-label span {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          font-weight: 500;
        }

        .file-name {
          margin-top: 8px;
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        /* Submit Button */
        .submit-btn {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border: none;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          box-shadow: 0 4px 20px rgba(43,103,119,0.3);
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 30px rgba(43,103,119,0.4);
        }

        .submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* Previous Applications Section */
        .applications-section {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 32px;
          box-shadow: 0 8px 32px rgba(43,103,119,0.1);
          border: 2px solid rgba(82,171,152,0.12);
          margin-bottom: 28px;
        }

        .applications-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 24px;
          padding-bottom: 20px;
          border-bottom: 2px solid rgba(43,103,119,0.1);
        }

        .applications-title {
          font-size: 22px;
          font-weight: 800;
          color: #2b6777;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .applications-count {
          background: linear-gradient(135deg, #c2edce 0%, #52ab98 100%);
          color: #2b6777;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 14px;
          font-weight: 600;
        }

        .refresh-btn {
          padding: 10px 20px;
          background: rgba(43,103,119,0.1);
          border: none;
          border-radius: 10px;
          color: #2b6777;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .refresh-btn:hover {
          background: rgba(43,103,119,0.15);
          transform: scale(1.05);
        }

        .applications-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .application-card {
          padding: 20px;
          border: 2px solid rgba(43,103,119,0.1);
          border-radius: 12px;
          background: white;
          transition: all 0.3s;
          cursor: pointer;
        }

        .application-card:hover {
          border-color: rgba(43,103,119,0.2);
          box-shadow: 0 4px 16px rgba(43,103,119,0.1);
          transform: translateY(-2px);
        }

        .application-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .application-type {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .application-type-icon {
          font-size: 24px;
        }

        .application-type-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .application-type-label {
          font-size: 16px;
          font-weight: 600;
          color: #2b6777;
          text-transform: capitalize;
        }

        .application-date-range {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
        }

        .application-status-badge {
          padding: 6px 14px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          color: white;
          text-transform: capitalize;
        }

        .application-card-body {
          padding-top: 16px;
          border-top: 1px solid rgba(43,103,119,0.1);
        }

        .application-reason {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          line-height: 1.6;
          margin-bottom: 12px;
        }

        .application-details {
          display: flex;
          flex-wrap: wrap;
          gap: 16px;
          font-size: 13px;
          color: rgba(43,103,119,0.6);
        }

        .application-detail-item {
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
          gap: 16px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(43,103,119,0.1);
          border-top: 4px solid #52ab98;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-text {
          font-size: 14px;
          color: rgba(43,103,119,0.6);
          font-weight: 500;
        }

        .empty-state {
          text-align: center;
          padding: 60px 20px;
        }

        .empty-state-icon {
          font-size: 64px;
          margin-bottom: 16px;
          opacity: 0.5;
        }

        .empty-state-title {
          font-size: 20px;
          font-weight: 600;
          color: #2b6777;
          margin-bottom: 8px;
        }

        .empty-state-text {
          font-size: 14px;
          color: rgba(43,103,119,0.6);
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes glow {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.mobile-open {
            transform: translateX(0);
          }

          .mobile-overlay {
            display: block;
          }

          .sidebar-top {
            display: flex;
          }

          .mobile-menu-toggle {
            display: flex;
          }

          .main-content {
            margin-left: 0;
            width: 100%;
          }

          .leave-type-grid {
            grid-template-columns: 1fr;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .hero-title {
            font-size: 24px;
          }

          .sidebar {
            width: 100%;
            max-width: 320px;
          }
        }

        @media (max-width: 768px) {
          .main-content {
            padding: 16px;
          }

          .hero-section {
            padding: 32px 24px;
          }

          .form-section {
            padding: 24px 20px;
          }

          .applications-section {
            padding: 24px 20px;
          }
        }
          .application-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
    margin-bottom: 16px;
    transition: all 0.2s;
}

.application-card:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    border-color: #d1d5db;
}

.application-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;
}

.application-type {
    display: flex;
    gap: 12px;
    flex: 1;
}

.application-type-icon {
    font-size: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.application-type-info {
    flex: 1;
}

.application-type-label {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
}

.application-date-range {
    font-size: 13px;
    color: #6b7280;
}

.application-status-badge {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: white;
    white-space: nowrap;
}

.application-card-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.application-reason {
    font-size: 14px;
    color: #374151;
    line-height: 1.5;
}

.application-details {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
}

.application-detail-item {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #6b7280;
}

.application-detail-item span:first-child {
    font-size: 14px;
}

      `}</style>

      <div className="dashboard-container">
        {/* Mobile Overlay */}
        <div
          className={`mobile-overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* FIXED SIDEBAR */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'S'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">{userInfo?.name || 'Student'}</div>
                <div className="user-role-sidebar">Student</div>
              </div>
            </div>
          </div>

          <div className="sidebar-nav">
            <div className="nav-section">
              <MenuItem icon="🏠" label="Dashboard" active={true} route="hostel-student-dashboard" />
              <MenuItem icon="🚪" label="Entry/Exit Log" route="hostel-student-entry-exit-log" />
              <MenuItem icon="🛏" label="Room Details" route="hostel-student-room-details" />
              <MenuItem icon="🍽️" label="Food Preference" route="hostel-student-food-preference" />
              <MenuItem icon="🧺" label="Laundry Service" route="hostel-student-laundry" />
              <MenuItem icon="💳" label="Grievance" route="hostel-student-payments" />
              <MenuItem icon="📝" label="Requests" route="hostel-student-requests" />
              <MenuItem icon="📋" label="Requisition" route="hostel-student-requisition" />
            </div>
          </div>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              <span>🚪</span>
              Logout
            </button>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="main-content">
          <div className="top-header">
            <div className="header-left">
              <button
                onClick={() => setSidebarOpen(true)}
                className="mobile-menu-toggle"
              >
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Welcome back,</div>
                <div className="header-name">{userInfo?.name || 'Student'}</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'S'}
              </div>
            </div>
          </div>

          <div className="content-wrapper">
            <div className="hero-section">
              <div className="hero-content">
                <div className="hero-title">Leave Application</div>
                <div className="hero-subtitle">Apply for leave and track your applications</div>
              </div>
            </div>

            <button
              onClick={() => setShowPreviousApplications(!showPreviousApplications)}
              className="view-toggle-btn"
            >
              <span>{showPreviousApplications ? '📝' : '📋'}</span>
              {showPreviousApplications ? 'Apply New Leave' : 'View Previous Applications'}
            </button>

            {success && (
              <div className="success-message">
                <span className="success-icon">✅</span>
                <span className="success-text">Leave application submitted successfully!</span>
              </div>
            )}

            {error && (
              <div className="error-message">
                <span className="error-icon">❌</span>
                <span className="error-text">{error}</span>
              </div>
            )}

            {!showPreviousApplications ? (
              <div className="form-section">
                <div className="form-header">
                  <div className="form-icon">📝</div>
                  <div className="form-title">Apply for Leave</div>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="leave-type-grid">
                    {leaveTypes.map((type) => (
                      <div
                        key={type.value}
                        className={`leave-type-option ${formData.leaveType === type.value ? 'selected' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, leaveType: type.value }))}
                      >
                        <div className="leave-type-icon">{type.icon}</div>
                        <div className="leave-type-label">{type.label}</div>
                        <div className="leave-type-desc">Apply for {type.label.toLowerCase()}</div>
                      </div>
                    ))}
                  </div>

                  <div className="form-grid">
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label required">From Date</label>
                        <input
                          type="date"
                          name="fromDate"
                          value={formData.fromDate}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label required">To Date</label>
                        <input
                          type="date"
                          name="toDate"
                          value={formData.toDate}
                          onChange={handleInputChange}
                          className="form-input"
                          required
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label required">Reason for Leave</label>
                      <textarea
                        name="reason"
                        value={formData.reason}
                        onChange={handleInputChange}
                        className="form-textarea"
                        placeholder="Please provide detailed reason for your leave application..."
                        required
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label">Contact Number</label>
                        <input
                          type="tel"
                          name="contactNumber"
                          value={formData.contactNumber}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Enter contact number"
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Emergency Contact</label>
                        <input
                          type="tel"
                          name="emergencyContact"
                          value={formData.emergencyContact}
                          onChange={handleInputChange}
                          className="form-input"
                          placeholder="Emergency contact number"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Address During Leave</label>
                      <textarea
                        name="addressDuringLeave"
                        value={formData.addressDuringLeave}
                        onChange={handleInputChange}
                        className="form-textarea"
                        placeholder="Enter your address during leave period..."
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Attachment (Optional)</label>
                      <div className="file-input-wrapper">
                        <input
                          type="file"
                          id="attachment"
                          name="attachment"
                          onChange={handleFileChange}
                          className="file-input"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        />
                        <label htmlFor="attachment" className="file-input-label">
                          <i>📎</i>
                          <span>Click to upload supporting document</span>
                          {formData.attachment && (
                            <div className="file-name">{formData.attachment.name}</div>
                          )}
                        </label>
                      </div>
                    </div>

                    <button type="submit" disabled={loading} className="submit-btn">
                      {loading ? (
                        <>
                          <div className="spinner"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <span>📤</span>
                          Submit Application
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="applications-section">
                <div className="applications-header">
                  <div className="applications-title">
                    <span>📋</span>
                    Previous Applications
                  </div>
                  <div className="applications-count">
                    {previousApplications.length} Application{previousApplications.length !== 1 ? 's' : ''}
                  </div>
                  <button onClick={fetchPreviousApplications} className="refresh-btn">
                    <span>🔄</span>
                    Refresh
                  </button>
                </div>

                {loadingApplications ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    <div className="loading-text">Loading applications...</div>
                  </div>
                ) : previousApplications.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-state-icon">📄</div>
                    <div className="empty-state-title">No Applications Found</div>
                    <div className="empty-state-text">You haven't submitted any leave applications yet.</div>
                  </div>
                ) : (
                  <div className="applications-list">
                    {previousApplications.map((application) => {
                      const rejectionInfo = getRejectionInfo(application);
                      const approvalTimeline = getApprovalTimeline(application);

                      return (
                        <div key={application.id} className="application-card">
                          <div className="application-card-header">
                            <div className="application-type">
                              <div className="application-type-icon">
                                {leaveTypes.find(type => type.value === application.leaveType)?.icon || '📄'}
                              </div>
                              <div className="application-type-info">
                                <div className="application-type-label">
                                  {leaveTypes.find(type => type.value === application.leaveType)?.label ||
                                    application.leaveType?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) ||
                                    'Leave'}
                                </div>
                                <div className="application-date-range">
                                  {formatDate(application.fromDate)} - {formatDate(application.toDate)}
                                  <span style={{ marginLeft: '8px', color: '#6b7280', fontSize: '0.9em' }}>
                                    ({calculateDuration(application.fromDate, application.toDate)})
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div
                              className="application-status-badge"
                              style={{ backgroundColor: getStatusColor(application.status) }}
                            >
                              {getStatusLabel(application.status)}
                            </div>
                          </div>

                          <div className="application-card-body">
                            <div className="application-reason">{application.reason}</div>

                            <div className="application-details">
                              <div className="application-detail-item">
                                <span>📅</span>
                                Applied on {formatDate(application.createdAt)}
                              </div>
                              {application.contactNumber && (
                                <div className="application-detail-item">
                                  <span>📞</span>
                                  {application.contactNumber}
                                </div>
                              )}
                              {application.destination && application.destination !== 'Not specified' && (
                                <div className="application-detail-item">
                                  <span>📍</span>
                                  {application.destination}
                                </div>
                              )}
                            </div>

                            {/* APPROVAL TIMELINE - Show for approved or partially approved applications */}
                            {approvalTimeline.length > 0 && (
                              <div style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid #e5e7eb'
                              }}>
                                <div style={{
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  color: '#374151',
                                  marginBottom: '8px'
                                }}>
                                  Approval Timeline
                                </div>
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '4px'
                                }}>
                                  {approvalTimeline.map((item, index) => (
                                    <div key={index} style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      fontSize: '12px',
                                      color: '#10b981'
                                    }}>
                                      <span style={{ marginRight: '6px', fontWeight: 'bold' }}>
                                        {item.icon}
                                      </span>
                                      <span>{item.label}</span>
                                      {item.date && (
                                        <span style={{
                                          marginLeft: '8px',
                                          color: '#6b7280',
                                          fontSize: '11px'
                                        }}>
                                          ({formatDate(item.date)})
                                        </span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* REJECTION INFO - Show for rejected applications */}
                            {rejectionInfo && (
                              <div style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid #fee2e2',
                                backgroundColor: '#fef2f2',
                                padding: '12px',
                                borderRadius: '8px',
                                marginLeft: '-12px',
                                marginRight: '-12px',
                                marginBottom: '-12px'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  marginBottom: '8px'
                                }}>
                                  <span style={{
                                    fontSize: '16px',
                                    marginRight: '8px'
                                  }}>
                                    ❌
                                  </span>
                                  <div>
                                    <div style={{
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      color: '#dc2626'
                                    }}>
                                      Rejected by {rejectionInfo.level}
                                    </div>
                                    <div style={{
                                      fontSize: '11px',
                                      color: '#991b1b',
                                      marginTop: '2px'
                                    }}>
                                      {rejectionInfo.rejectedBy}
                                      {rejectionInfo.rejectedAt && (
                                        <span style={{ marginLeft: '8px' }}>
                                          • {formatDate(rejectionInfo.rejectedAt)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {rejectionInfo.remarks && (
                                  <div style={{
                                    fontSize: '12px',
                                    color: '#7f1d1d',
                                    backgroundColor: '#fee2e2',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    borderLeft: '3px solid #dc2626',
                                    marginTop: '8px'
                                  }}>
                                    <div style={{
                                      fontWeight: '600',
                                      marginBottom: '4px',
                                      fontSize: '11px',
                                      textTransform: 'uppercase',
                                      letterSpacing: '0.5px'
                                    }}>
                                      Reason for Rejection:
                                    </div>
                                    <div>
                                      {rejectionInfo.remarks}
                                    </div>
                                  </div>
                                )}

                                {/* Show what was approved before rejection */}
                                {approvalTimeline.length > 0 && (
                                  <div style={{
                                    marginTop: '12px',
                                    paddingTop: '8px',
                                    borderTop: '1px solid #fecaca'
                                  }}>
                                    <div style={{
                                      fontSize: '11px',
                                      color: '#991b1b',
                                      marginBottom: '6px',
                                      fontWeight: '600'
                                    }}>
                                      Previous Approvals:
                                    </div>
                                    <div style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: '4px'
                                    }}>
                                      {approvalTimeline.map((item, index) => (
                                        <div key={index} style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          fontSize: '11px',
                                          color: '#059669',
                                          opacity: 0.7
                                        }}>
                                          <span style={{ marginRight: '6px' }}>
                                            {item.icon}
                                          </span>
                                          <span>{item.label}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* CANCELLED INFO - Show for cancelled applications */}
                            {application.status === 'cancelled' && (
                              <div style={{
                                marginTop: '12px',
                                paddingTop: '12px',
                                borderTop: '1px solid #e5e7eb',
                                backgroundColor: '#f9fafb',
                                padding: '12px',
                                borderRadius: '8px',
                                marginLeft: '-12px',
                                marginRight: '-12px',
                                marginBottom: '-12px'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: '12px',
                                  color: '#6b7280'
                                }}>
                                  <span style={{ marginRight: '8px', fontSize: '16px' }}>🚫</span>
                                  <span>Cancelled by student</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}

                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LeaveApply;
